import { useState } from 'react';

import { ArrowLeft, Loader2, Play, Save } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import {
  useCreateMetricDefinition,
  useMetricDefinitionDetail,
  useTestPredicate,
  useUpdateMetricDefinition,
} from '@/hooks/useMetricDefinitions';
import { usePageTitle } from '@/hooks/usePageTitle';

import type { Predicate } from '@/lib/api/metric-definitions.api';

const SAMPLE_PREDICATE = JSON.stringify(
  {
    and: [
      { field: 'is_engaged', eq: true },
      { field: 'outcome', eq: 'LEAD_CAPTURED' },
      { label: 'car_chosen', eq: true },
    ],
  },
  null,
  2,
);

const KEY_RE = /^[a-zA-Z_][a-zA-Z0-9_]{0,99}$/;

export const MetricDefinitionEditPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { key: routeKey } = useParams<{ key: string }>();
  const isNew = !routeKey || routeKey === 'new';

  usePageTitle(isNew ? 'New Metric' : `Metric: ${routeKey}`);

  const { data: detail, isLoading } = useMetricDefinitionDetail(isNew ? undefined : routeKey);
  const createMutation = useCreateMetricDefinition();
  const updateMutation = useUpdateMetricDefinition();
  const testMutation = useTestPredicate();

  const [key, setKey] = useState('');
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [predicateText, setPredicateText] = useState(SAMPLE_PREDICATE);
  const [predicateError, setPredicateError] = useState<string | null>(null);
  const [testCount, setTestCount] = useState<number | null>(null);

  // Sync local form state from server-loaded detail (React-recommended pattern
  // for "adjust state when a derived value changes").
  const [prevDetail, setPrevDetail] = useState(detail);
  if (detail !== prevDetail) {
    setPrevDetail(detail);
    if (detail) {
      setKey(detail.key);
      setDescription(detail.description);
      setTagsInput(detail.tags.join(', '));
      setEnabled(detail.enabled);
      setPredicateText(JSON.stringify(detail.predicate, null, 2));
    }
  }

  const parsePredicate = (): Predicate | null => {
    try {
      const parsed = JSON.parse(predicateText);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        setPredicateError('Predicate must be a JSON object');
        return null;
      }
      setPredicateError(null);
      return parsed as Predicate;
    } catch (err) {
      setPredicateError(err instanceof Error ? err.message : 'Invalid JSON');
      return null;
    }
  };

  const tags = tagsInput
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

  const handleTest = async () => {
    const predicate = parsePredicate();
    if (!predicate) return;
    setTestCount(null);
    try {
      const result = await testMutation.mutateAsync({ predicate });
      setTestCount(result.count);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Test failed';
      toast({ variant: 'destructive', title: 'Test failed', description: msg });
    }
  };

  const handleSave = async () => {
    const predicate = parsePredicate();
    if (!predicate) return;

    if (isNew && !KEY_RE.test(key)) {
      toast({
        variant: 'destructive',
        title: 'Invalid key',
        description: 'Key must start with a letter or underscore, alphanumeric/underscore only.',
      });
      return;
    }
    if (!description.trim()) {
      toast({
        variant: 'destructive',
        title: 'Missing description',
        description: 'Add a description.',
      });
      return;
    }

    try {
      if (isNew) {
        await createMutation.mutateAsync({ key, description, tags, predicate, enabled });
        toast({ variant: 'success', title: 'Created', description: `Metric '${key}' created.` });
      } else {
        await updateMutation.mutateAsync({
          key: routeKey,
          data: { description, tags, predicate, enabled },
        });
        toast({ variant: 'success', title: 'Saved', description: `Metric '${routeKey}' updated.` });
      }
      navigate('/metric-definitions');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Save failed';
      toast({ variant: 'destructive', title: 'Save failed', description: msg });
    }
  };

  if (!isNew && isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/metric-definitions')}>
        <ArrowLeft className="mr-2 h-4 w-4" /> Back
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>{isNew ? 'New metric' : `Edit metric: ${routeKey}`}</CardTitle>
          <CardDescription>
            Define a metric by predicate. The DSL is documented in
            apps/backend/src/modules/analytics/dsl/types.ts. Use the &quot;Test count&quot; button
            to verify the predicate runs and returns a sensible count.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isNew && (
            <div className="space-y-1">
              <label className="text-sm font-medium">Key</label>
              <Input
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="my_metric_key"
                className="font-mono text-xs"
              />
              <p className="text-muted-foreground text-xs">
                Lowercase letters/digits/underscores. Must not collide with a CODE_METRICS key.
              </p>
            </div>
          )}

          <div className="space-y-1">
            <label className="text-sm font-medium">Description</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Sales pipeline calls where the AI explained the V12 USP"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Tags (comma-separated)</label>
            <Input
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="sales, quality"
            />
            <div className="flex flex-wrap gap-1 pt-1">
              {tags.map((t) => (
                <Badge key={t} variant="outline" className="text-[10px]">
                  {t}
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span className="text-sm">{enabled ? 'Enabled' : 'Disabled'}</span>
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Predicate (JSON)</label>
              <Button
                size="sm"
                variant="outline"
                onClick={handleTest}
                disabled={testMutation.isPending}
              >
                <Play className="mr-2 h-3.5 w-3.5" />
                {testMutation.isPending ? 'Testing…' : 'Test count'}
              </Button>
            </div>
            <textarea
              value={predicateText}
              onChange={(e) => {
                setPredicateText(e.target.value);
                setTestCount(null);
              }}
              rows={16}
              className="border-input bg-background ring-offset-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 font-mono text-xs focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
            />
            {predicateError && <p className="text-destructive text-xs">{predicateError}</p>}
            {testCount !== null && (
              <p className="text-muted-foreground text-xs">
                Test count:{' '}
                <strong className="text-foreground">{testCount.toLocaleString()}</strong> rows match
              </p>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => navigate('/metric-definitions')}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {createMutation.isPending || updateMutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
